import {
  DEFAULT_DECK_CONFIG,
  isDeckSize,
  DEFAULT_HOUSE_RULES,
  newMatchState,
  normalizeHouseRules,
  tableHouseRules,
  normalizeDeckConfig,
  normalizeWilds,
  type AIDifficulty,
  type CardMemorySnapshot,
  type DeckConfig,
  type GameState,
  type HouseRules,
  type MatchState,
  isTurnInProgress,
} from "@fiveout/core";
import type { RoundTally } from "../stats/recorder";
import { newRoundTally } from "../stats/recorder";

/**
 * Local-play persistence.
 *
 * Two separate things live here:
 *
 *  1. The table CONFIG (seats, deck, timer), remembered between rounds so the
 *     setup screen comes back the way you left it.
 *  2. The in-progress ROUND, so a browser refresh resumes exactly where you were
 *     instead of losing the game.
 *
 * (2) means deck order is written to localStorage, which a player with devtools
 * could read to see their own upcoming cards. That's a deliberate, accepted
 * trade for local play only — resumability was judged worth more than making
 * local self-cheating slightly harder. Online is unaffected: the server never
 * sends deck order and nothing about an online game is stored here.
 *
 * The RNG is saved as a seed plus a step count rather than an opaque object, so
 * a resumed round produces exactly the same AI decisions and timeout moves it
 * would have without the refresh.
 */

// Bumped to 2 for Jourdan's Streaks: a round saved at 1 has no streak counters
// and no lock list, and resuming one would deal a board the rule cannot read.
export const SAVE_SCHEMA_VERSION = 2;

const CONFIG_KEY = "fiveout:localConfig";
const SAVE_KEY = "fiveout:localSave";
const TUTORIAL_KEY = "fiveout:tutorialSeen";

export interface StoredSeat {
  id: string;
  /**
   * What the seat is called at the table.
   *
   * Typed by the player on a human seat. DERIVED on a CPU seat — see
   * `withCpuNames`, which is run over the roster after every change to it, so
   * this field is never the source of truth for one.
   */
  nickname: string;
  kind: "human" | "ai";
  aiDifficulty: AIDifficulty;
}

/**
 * What a CPU seat is called: `CPU`, the difficulty's initial, and its position
 * among the CPUs at the table — `CPUM1`, `CPUH2`.
 *
 * Named rather than nameable because the name is information. A CPU's
 * difficulty is otherwise invisible once the cards are dealt: the setup screen
 * is the only place it is shown, and by the time a player is wondering why one
 * opponent keeps guessing right, they are three screens away from it. Letting
 * the name be edited would mean the one place that fact is legible is also the
 * one a player can delete by accident.
 */
export function cpuNickname(difficulty: StoredSeat["aiDifficulty"], ordinal: number): string {
  return `CPU${difficulty[0].toUpperCase()}${ordinal}`;
}

/**
 * Re-derives every CPU seat's name, leaving human seats alone.
 *
 * Run over the WHOLE roster after any change to it rather than at the point a
 * seat is added, because the numbering is positional: removing CPU 1 of 3 has to
 * renumber the two behind it, and changing a difficulty renames in place. Names
 * a stored config brought back from before this rule are corrected on load for
 * the same reason.
 */
export function withCpuNames(seats: StoredSeat[]): StoredSeat[] {
  let cpuOrdinal = 0;
  return seats.map((seat) => {
    if (seat.kind !== "ai") return seat;
    cpuOrdinal++;
    const nickname = cpuNickname(seat.aiDifficulty, cpuOrdinal);
    return seat.nickname === nickname ? seat : { ...seat, nickname };
  });
}

export interface StoredLocalConfig {
  schemaVersion: number;
  seats: StoredSeat[];
  deckConfig: DeckConfig;
  houseRules: HouseRules;
  turnTimerSeconds: number | null;
}

/** Default table when nothing has been saved: 1 player, half deck, every wild on, default house rules. */
export function defaultLocalConfig(): StoredLocalConfig {
  return {
    schemaVersion: SAVE_SCHEMA_VERSION,
    seats: [{ id: "p1", nickname: "Player 1", kind: "human", aiDifficulty: "medium" }],
    deckConfig: { ...DEFAULT_DECK_CONFIG, wilds: { ...DEFAULT_DECK_CONFIG.wilds } },
    houseRules: { ...DEFAULT_HOUSE_RULES },
    turnTimerSeconds: null,
  };
}

export function loadLocalConfig(): StoredLocalConfig {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    if (!raw) return defaultLocalConfig();
    const parsed = JSON.parse(raw) as Partial<StoredLocalConfig>;
    const fallback = defaultLocalConfig();
    const size = parsed.deckConfig?.size;
    const deckConfig: DeckConfig =
      size !== undefined && isDeckSize(size)
        ? normalizeDeckConfig({ size, wilds: normalizeWilds(parsed.deckConfig?.wilds ?? fallback.deckConfig.wilds) })
        : fallback.deckConfig;
    return {
      schemaVersion: SAVE_SCHEMA_VERSION,
      seats: withCpuNames(parsed.seats?.length ? parsed.seats : fallback.seats),
      deckConfig,
      // Only Blue Shell is a choice now; a config saved when Vinny's Rule or
      // Streaks could be switched off comes back with them on.
      houseRules: tableHouseRules(parsed.houseRules),
      turnTimerSeconds: parsed.turnTimerSeconds ?? null,
    };
  } catch {
    return defaultLocalConfig();
  }
}

export function saveLocalConfig(config: StoredLocalConfig): void {
  try {
    localStorage.setItem(CONFIG_KEY, JSON.stringify({ ...config, schemaVersion: SAVE_SCHEMA_VERSION }));
  } catch {
    /* best effort */
  }
}

const QUICK_PLAY_KEY = "fiveout:quickPlay";

/**
 * The table Quick Play deals, set in Settings. Only the table: Quick Play is
 * always one player, so there are no seats to store, and the seat's name is the
 * signed-in username at the moment of the deal (see `quickPlayTable`).
 *
 * Kept apart from `StoredLocalConfig` on purpose. Play Locally's table is what
 * the host last built and changes with every visit; this one is a standing
 * preference, so a hot-seat evening must not quietly rewrite the one-tap game.
 * And unlike the setup screen's wilds, it is NOT reset to all-on each time - a
 * preference that forgot itself would not be one.
 */
export interface StoredQuickPlay {
  deckConfig: DeckConfig;
  houseRules: HouseRules;
  turnTimerSeconds: number | null;
}

/** One deck, every wild, Constellations with every event, no clock. */
export function defaultQuickPlay(): StoredQuickPlay {
  return {
    deckConfig: { size: 1, wilds: { smash: true, lightning: true, swap: true, peek: true } },
    houseRules: tableHouseRules({ ...DEFAULT_HOUSE_RULES, starfall: true, starfallOff: [] }),
    turnTimerSeconds: null,
  };
}

export function loadQuickPlay(): StoredQuickPlay {
  try {
    const raw = localStorage.getItem(QUICK_PLAY_KEY);
    if (!raw) return defaultQuickPlay();
    const parsed = JSON.parse(raw) as Partial<StoredQuickPlay>;
    const fallback = defaultQuickPlay();
    const size = parsed.deckConfig?.size;
    return {
      deckConfig:
        size !== undefined && isDeckSize(size)
          ? normalizeDeckConfig({ size, wilds: normalizeWilds(parsed.deckConfig?.wilds ?? fallback.deckConfig.wilds) })
          : fallback.deckConfig,
      houseRules: tableHouseRules(parsed.houseRules ?? fallback.houseRules),
      turnTimerSeconds: typeof parsed.turnTimerSeconds === "number" ? parsed.turnTimerSeconds : null,
    };
  } catch {
    return defaultQuickPlay();
  }
}

export function saveQuickPlay(config: StoredQuickPlay): void {
  try {
    localStorage.setItem(QUICK_PLAY_KEY, JSON.stringify(config));
  } catch {
    /* best effort */
  }
}

/**
 * The whole table Quick Play deals: one human seat in front of `quick`. Seat one
 * is the device owner (it wears their crest), so it carries their username.
 */
export function quickPlayTable(quick: StoredQuickPlay, nickname: string | null): StoredLocalConfig {
  return {
    schemaVersion: SAVE_SCHEMA_VERSION,
    seats: [{ id: "p1", nickname: nickname?.trim() || "Player 1", kind: "human", aiDifficulty: "medium" }],
    deckConfig: quick.deckConfig,
    houseRules: quick.houseRules,
    turnTimerSeconds: quick.turnTimerSeconds,
  };
}

/** Everything needed to resume a round exactly as it was. */
export interface StoredRound {
  schemaVersion: number;
  /** Stable id for this round, used to keep stats idempotent across refreshes. */
  roundId: string;
  gameState: GameState;
  match: MatchState;
  config: StoredLocalConfig;
  /** RNG seed and how many values have been drawn from it. */
  rngSeed: number;
  rngSteps: number;
  turnCount: number;
  tally: RoundTally;
  /** What the Hard AI has observed, so it doesn't lose its memory on refresh. */
  memory: CardMemorySnapshot;
  savedAtMs: number;
}

/**
 * Which saved rounds a screen can pick up: one still being played (the home
 * screen's Resume), one that has ENDED (the score pad), or either (the table,
 * which a reload can land on before or after the last card).
 */
export type ResumableRound = "in-play" | "finished" | "either";

/**
 * The saved round, if it is the kind `which` asks for.
 *
 * A finished round is kept rather than cleared so that a reload at the end of
 * a round - iOS throwing away a home-screen app's page under memory pressure
 * lands exactly there - comes back to that round's podium and score pad, with
 * the match it belongs to, instead of "No round in progress" and a player
 * wondering what was lost. The default is only a round still in play, which is
 * what Resume offers; the score pad must not take one, or reading the save
 * would set CPU turns playing behind it.
 */
export function loadRound(which: ResumableRound = "in-play"): StoredRound | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredRound;
    if (parsed.schemaVersion !== SAVE_SCHEMA_VERSION) return null;
    if (!parsed.gameState || !parsed.roundId) return null;
    const inPlay = isTurnInProgress(parsed.gameState.phase);
    const finished = isRoundFinished(parsed.gameState.phase);
    if (!(which === "either" ? inPlay || finished : which === "finished" ? finished : inPlay)) return null;
    // Spread over the defaults rather than replacing only when absent: a save
    // written before a counter was added still has a tally, just one missing
    // that field, and `undefined++` would poison it with NaN for the round.
    parsed.tally = { ...newRoundTally(), ...(parsed.tally ?? {}) };
    // Same reason as the tally above: a round saved before house rules existed
    // carries no block, and the engine reads it on every move.
    parsed.gameState.houseRules = normalizeHouseRules(parsed.gameState.houseRules);
    parsed.config.houseRules = tableHouseRules(parsed.config.houseRules);
    // Streaks used to shut piles for the whole table, with the lock naming the
    // player who SPENT it; they now name the player they are against. A round
    // saved under the old reading would hand somebody else's penalty to the
    // wrong seat, so any lock in a save is dropped and the board comes back
    // open. Nothing is lost that this round would have kept for more than a
    // turn, and no mark is pending in a save written before marks existed.
    parsed.gameState.streakLocks = [];
    parsed.gameState.streakTargets = parsed.gameState.streakTargets ?? [];
    // Swap and Peek arrived without a schema bump because a save from before
    // them is still a playable round: its pool has neither kind, so nobody can
    // be peeking and nothing can be owed. The fields are filled rather than
    // left absent so the wild config reads as "off" (not undefined) wherever
    // the UI inspects it, and so nothing downstream has to know saves predate them.
    parsed.gameState.deckConfig.wilds = normalizeWilds(parsed.gameState.deckConfig.wilds);
    // Shape only, deliberately, for the ROUND: its pool was built and dealt under
    // whatever rule was in force at the time, and a half deck dealt before the
    // two-kind limit really does have four kinds in it. Trimming the config would
    // make the rules sheet deny cards the player is holding. The table CONFIG
    // beside it is the preference for the next round, so that one is trimmed.
    parsed.config.deckConfig = normalizeDeckConfig(parsed.config.deckConfig);
    for (const player of parsed.gameState.players) player.peeking = player.peeking ?? false;
    parsed.gameState.swapsOwed = parsed.gameState.swapsOwed ?? 0;
    if (!parsed.match) parsed.match = newMatchState(parsed.roundId);
    return parsed;
  } catch {
    return null;
  }
}

export function saveRound(round: StoredRound): void {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify({ ...round, schemaVersion: SAVE_SCHEMA_VERSION, savedAtMs: Date.now() }));
  } catch {
    /* storage full — the round simply won't be resumable */
  }
}

/** A round that has ended: its scores are in and nobody has a turn. */
export function isRoundFinished(phase: GameState["phase"]): boolean {
  return phase === "round-over" || phase === "game-over";
}

export function clearRound(): void {
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch {
    /* best effort */
  }
}

/** True when there's a resumable round waiting. Used by the home screen. */
export function hasResumableRound(): boolean {
  return loadRound() !== null;
}

/**
 * Whether this device has been offered the tutorial.
 *
 * Set when the tutorial screen opens rather than when it is finished, because
 * it gates one thing only: the automatic first-run entry. Someone who skipped
 * on the welcome card has answered that question, and asking again on their
 * next launch would read as the app not listening. It stays reachable for good
 * under How to Play.
 */
export function hasSeenTutorial(): boolean {
  try {
    return localStorage.getItem(TUTORIAL_KEY) === "1";
  } catch {
    // No storage (private mode, blocked cookies): treat it as seen. Dropping a
    // player into the tutorial on every single launch is far worse than never.
    return true;
  }
}

export function markTutorialSeen(): void {
  try {
    localStorage.setItem(TUTORIAL_KEY, "1");
  } catch {
    /* best effort */
  }
}
